import { useEffect, useMemo, useState } from "react";
import { Clock3, RotateCcw } from "lucide-react";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { Switch } from "../../components/ui/switch";
import { useGroupTier } from "../../hooks/useGroupTier";
import { cn } from "../../lib/utils";
import {
  AUTO_NAG_MAX_INTERVAL_DAYS,
  AUTO_NAG_MAX_INTERVAL_HOURS,
  AUTO_NAG_MIN_INTERVAL_DAYS,
  AUTO_NAG_MIN_INTERVAL_HOURS,
  createAutoNagSettingsSnapshot,
  createDefaultAutoNagSettings,
  normalizeAutoNagSettings,
  resolveAutoNagCutoff,
  selectNextAutoNagAt,
  validateAutoNagAuthoring,
} from "./auto-nag";
import { autoNagSummary } from "./auto-nag-summary";

function toLocalInput(date, timeZone) {
  try {
    return formatInTimeZone(date, timeZone, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return "";
  }
}

function formatStatusDate(value, timeZone) {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date || !Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function AutoNagSettings({
  group,
  value,
  onChange,
  timeZone,
  votingDeadlineAt = null,
  earliestSlotAt = null,
  nagState = null,
  className = "",
  headingId = "auto-nag-settings-heading",
  onValidationChange = null,
}) {
  const fallback = useMemo(
    () => createDefaultAutoNagSettings({ timeZone }),
    [timeZone]
  );
  const normalized = normalizeAutoNagSettings(value);
  const settings = normalized.valid && normalized.settings
    ? normalized.settings
    : fallback;
  const settingsKey = `${settings.firstSendAt.getTime()}:${settings.interval.value}:${settings.interval.unit}:${settings.timeZone}`;
  const [intervalDraft, setIntervalDraft] = useState(null);
  const [firstSendDraft, setFirstSendDraft] = useState(null);
  const intervalInput = intervalDraft?.settingsKey === settingsKey
    ? intervalDraft.value
    : String(settings.interval.value);
  const normalizedFirstSendLocal = toLocalInput(settings.firstSendAt, settings.timeZone);
  const firstSendLocal = firstSendDraft?.settingsKey === settingsKey
    ? firstSendDraft.value
    : normalizedFirstSendLocal;
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState(null);
  const tier = useGroupTier(group?.creatorId || null);
  const entitled = Boolean(group) && !tier.loading && tier.canUseFeature("AUTOMATED_REMINDERS");
  const canEdit = Boolean(group) && (tier.loading || entitled);
  const cutoff = resolveAutoNagCutoff({ votingDeadlineAt, earliestSlotAt });
  const authoringValidation = validateAutoNagAuthoring({
    config: settings,
    votingDeadlineAt,
    earliestSlotAt,
  });

  const update = (next) => {
    setError("");
    setErrorField(null);
    const snapshot = createAutoNagSettingsSnapshot(next, { timeZone });
    const validation = validateAutoNagAuthoring({
      config: snapshot,
      votingDeadlineAt,
      earliestSlotAt,
    });
    onValidationChange?.(validation.valid);
    onChange(snapshot);
  };

  const parseFirstSend = () => {
    if (!firstSendLocal) return null;
    try {
      const parsed = fromZonedTime(firstSendLocal, settings.timeZone);
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    } catch {
      return null;
    }
  };

  const validateFirstSend = (currentTime) => {
    const firstSendAt = parseFirstSend();
    if (!firstSendAt) {
      setError("Choose a valid first auto-nag date and time.");
      setErrorField("firstSend");
      return null;
    }
    if (firstSendAt.getTime() <= currentTime.getTime()) {
      setError("The first auto-nag must be in the future.");
      setErrorField("firstSend");
      return null;
    }
    if (cutoff && firstSendAt.getTime() >= cutoff.getTime()) {
      setError("The first auto-nag must be before voting closes or the first proposed session time.");
      setErrorField("firstSend");
      return null;
    }
    return firstSendAt;
  };

  const commitInterval = () => {
    const nextValue = Number(intervalInput);
    const min = settings.interval.unit === "hours"
      ? AUTO_NAG_MIN_INTERVAL_HOURS
      : AUTO_NAG_MIN_INTERVAL_DAYS;
    const max = settings.interval.unit === "hours"
      ? AUTO_NAG_MAX_INTERVAL_HOURS
      : AUTO_NAG_MAX_INTERVAL_DAYS;
    if (!Number.isInteger(nextValue) || nextValue < min || nextValue > max) {
      setError(settings.interval.unit === "hours"
        ? "Hour cadence must be a whole number from 12 to 720."
        : "Day cadence must be a whole number from 1 to 30.");
      setErrorField("cadence");
      setIntervalDraft(null);
      return;
    }
    update({ ...settings, interval: { ...settings.interval, value: nextValue } });
  };

  const nextSend = settings.enabled ? selectNextAutoNagAt({ config: settings }) : null;
  const lastSent = formatStatusDate(nagState?.lastSentAt, settings.timeZone);
  const derivedError = settings.enabled && !authoringValidation.valid
    ? authoringValidation.error
    : "";
  const displayedError = error || derivedError;
  const displayedErrorField = error ? errorField : derivedError ? "firstSend" : null;

  useEffect(() => {
    onValidationChange?.(!displayedError);
  }, [displayedError, onValidationChange]);

  return (
    <section
      className={cn(
        "rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/20",
        className
      )}
      aria-labelledby={headingId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-2.5">
          <span className="mt-0.5 self-start rounded-lg bg-amber-100 p-2 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200">
            <Clock3 className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p id={headingId} className="text-xs font-semibold text-card-foreground">
              Auto-nag non-voters
            </p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              Repeats until everyone finishes the session vote and all still-open required add-on polls, or voting closes.
            </p>
          </div>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(enabled) => {
            if (enabled) {
              const firstSendAt = parseFirstSend();
              const candidate = { ...settings, enabled: true, firstSendAt };
              const validation = validateAutoNagAuthoring({
                config: candidate,
                votingDeadlineAt,
                earliestSlotAt,
              });
              if (!validation.valid) {
                setError(validation.error);
                setErrorField("firstSend");
                return;
              }
              update(candidate);
            } else {
              update({ ...settings, enabled: false });
            }
          }}
          disabled={!canEdit}
          aria-label="Auto-nag non-voters"
        />
      </div>

      {!group ? (
        <p className="mt-3 rounded-lg bg-card px-3 py-2 text-xs text-muted-foreground">
          Choose a questing group before enabling auto-nag.
        </p>
      ) : null}

      <div className="mt-3 min-w-0 rounded-lg bg-card/80 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-card-foreground">
            {autoNagSummary(settings)}
          </p>
          {!settings.enabled ? (
            <span className="text-[11px] text-muted-foreground">Schedule retained while paused</span>
          ) : null}
        </div>
        <div className="mt-3 grid min-w-0 gap-3 sm:grid-cols-2">
          <label className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            First send
            <input
              type="datetime-local"
              value={firstSendLocal}
              onChange={(event) => {
                setFirstSendDraft({ settingsKey, value: event.target.value });
                setError("");
                setErrorField(null);
                onValidationChange?.(false);
              }}
              onBlur={() => {
                const firstSendAt = validateFirstSend(new Date());
                if (firstSendAt) update({ ...settings, firstSendAt });
              }}
              disabled={!canEdit}
              aria-label="First auto-nag"
              aria-invalid={displayedErrorField === "firstSend" || undefined}
              aria-describedby={
                displayedErrorField === "firstSend" ? "auto-nag-first-send-error" : undefined
              }
              className="mt-1 min-h-[40px] min-w-0 w-full max-w-full rounded-xl border border-input bg-card px-2 text-xs font-normal text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 sm:px-3 sm:text-sm"
            />
          </label>
          <div className="grid min-w-0 gap-2 min-[480px]:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
            <label className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Every
              <input
                type="number"
                min={settings.interval.unit === "hours" ? 12 : 1}
                max={settings.interval.unit === "hours" ? 720 : 30}
                step="1"
                value={intervalInput}
                onChange={(event) => {
                  setIntervalDraft({ settingsKey, value: event.target.value });
                  setError("");
                  setErrorField(null);
                  onValidationChange?.(false);
                }}
                onBlur={commitInterval}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitInterval();
                  }
                }}
                disabled={!canEdit}
                aria-label="Auto-nag cadence value"
                className="mt-1 min-h-[40px] min-w-0 w-full max-w-full rounded-xl border border-input bg-card px-3 text-sm font-normal text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
            </label>
            <label className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Unit
              <select
                value={settings.interval.unit}
                onChange={(event) => {
                  const unit = event.target.value;
                  const value = unit === "hours"
                    ? Math.max(AUTO_NAG_MIN_INTERVAL_HOURS, Number(intervalInput) || 0)
                    : Math.max(AUTO_NAG_MIN_INTERVAL_DAYS, Math.min(30, Number(intervalInput) || 1));
                  setIntervalDraft({ settingsKey, value: String(value) });
                  update({ ...settings, interval: { value, unit } });
                }}
                disabled={!canEdit}
                aria-label="Auto-nag cadence unit"
                className="mt-1 min-h-[40px] min-w-0 w-full max-w-full rounded-xl border border-input bg-card px-3 text-sm font-normal text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="hours">hours</option>
                <option value="days">days at this time</option>
              </select>
            </label>
          </div>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Uses {settings.timeZone}. The minimum cadence is every 12 hours. Manual Nudge is unavailable while auto-nag is on.
        </p>
        {settings.enabled && nextSend ? (
          <p className="mt-2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            Next send: {formatStatusDate(nextSend, settings.timeZone)}
          </p>
        ) : null}
        {lastSent ? (
          <p className="mt-1 text-[11px] text-muted-foreground">Last sent: {lastSent}</p>
        ) : null}
        {displayedError ? (
          <p
            id={displayedErrorField === "firstSend" ? "auto-nag-first-send-error" : undefined}
            role="alert"
            className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200"
          >
            {displayedError}
          </p>
        ) : null}
        {!normalized.valid && value != null ? (
          <button
            type="button"
            onClick={() => {
              const reset = createDefaultAutoNagSettings({ timeZone });
              setFirstSendDraft(null);
              setIntervalDraft(null);
              update(reset);
            }}
            className="mt-2 inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Repair schedule
          </button>
        ) : null}
      </div>
    </section>
  );
}
